import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prayer_app_token';

const getAuthHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const getTodayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const QazaContext = createContext(null);

export const useQaza = () => {
  const ctx = useContext(QazaContext);
  if (!ctx) throw new Error('useQaza must be used within a QazaProvider');
  return ctx;
};

export const QazaProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!isAuthenticated) { setLogs([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/qaza`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
      }
    } catch (e) {
      console.error('Failed to fetch qaza logs:', e);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const addQaza = useCallback(async (prayer, originalDate) => {
    if (!isAuthenticated) return null;
    try {
      const res = await fetch(`${BACKEND_URL}/api/qaza`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ prayer, original_date: originalDate }),
      });
      if (res.ok) { await fetchLogs(); return true; }
    } catch (e) { console.error('Failed to add qaza log:', e); }
    return false;
  }, [isAuthenticated, fetchLogs]);

  /**
   * Mark qaza as made up.
   * If the original_date is today, also marks the prayer as complete in prayer_tracker.
   * onTrackerRefresh: optional callback to trigger TrackerContext refresh.
   */
  const makeUpQaza = useCallback(async (logId, onTrackerRefresh) => {
    if (!isAuthenticated) return;
    const log = logs.find(l => l.log_id === logId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/qaza/${logId}/makeup`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        // If this qaza is for today, also mark the prayer as complete in tracker
        if (log && log.original_date === getTodayKey()) {
          await fetch(`${BACKEND_URL}/api/tracker/prayer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({ prayer: log.prayer, completed: true, date: log.original_date }),
          });
          if (onTrackerRefresh) onTrackerRefresh();
        }
        await fetchLogs();
      }
    } catch (e) { console.error('Failed to make up qaza:', e); }
  }, [isAuthenticated, fetchLogs, logs]);

  const removeQaza = useCallback(async (logId) => {
    if (!isAuthenticated) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/qaza/${logId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) await fetchLogs();
    } catch (e) { console.error('Failed to remove qaza log:', e); }
  }, [isAuthenticated, fetchLogs]);

  const pending = logs.filter(l => !l.made_up);
  const madeUp = logs.filter(l => l.made_up);

  /** Prayer logged as Qaza (pending) for a given date */
  const isTodayQaza = (prayer, dateKey) =>
    pending.some(l => l.prayer === prayer && l.original_date === dateKey);

  /** Prayer was Qaza but has been made up for a given date */
  const isTodayQazaMadeUp = (prayer, dateKey) =>
    madeUp.some(l => l.prayer === prayer && l.original_date === dateKey);

  return (
    <QazaContext.Provider value={{
      logs, pending, madeUp, loading,
      addQaza, makeUpQaza, removeQaza,
      isTodayQaza, isTodayQazaMadeUp,
      refetch: fetchLogs,
    }}>
      {children}
    </QazaContext.Provider>
  );
};
